import React, { useState, useContext, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Ticket, TicketStatus } from "@services/tickets/types";
import { putTicket } from "@services/tickets";
import { Priority } from "@services/common/types";
import { useErrorNotification } from "@common/components/Notification";
import { FormContext } from "../context/formContext";
import Button from "@common/components/Button";
import { TicketListContext } from "@pages/Tickets/context";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";

interface ReopenCaseDialogProps {
  open?: boolean;
  onClose?: (saved?: boolean) => void;
}

const ReopenCaseDialog = (props: ReopenCaseDialogProps) => {
  const { open = false, onClose = () => {} } = props;
  const { dispatchTicket, form, reset } = useContext(FormContext);
  const { dispatch } = useContext(TicketListContext);
  const { detail } = useContext(RoomsRealtimeContext);
  const { ticket, details } = form;
  const { t } = useTranslation();
  const dispatchError = useErrorNotification();
  const [comment, setComment] = useState<string>("");
  const [errorComment, setErrorComment] = useState<string>("");

  const handleIssueChange = (value: string) => {
    setComment(value);
    setErrorComment("");
  };

  const onConfirm = useCallback(() => {
    setErrorComment("");
    if (comment != "") {
      var event = {
        subject: ticket.issue?.issue,
        description: t("Case reopened") + (comment ? ": " : "") + comment,
        priority: "Informational" as Priority,
        createDate: new Date().getTime(),
      };
      delete form.ticket.issue?.resolution;
      delete form.ticket.issue?.comment;
      delete form.details?.closedBy;
      details?.events?.push(event);
      dispatchTicket({ status: "Open" as TicketStatus });
      try {
        (async () => {
          await putTicket({
            ...form.ticket,
            status: "Open" as TicketStatus,
            details: form.details,
          } as Ticket);
          setComment("");
          dispatch("updateTicket");
          detail?.refetch();
          reset();
          onClose(true);
        })();
      } catch (e) {
        dispatchError(e);
      }
    } else {
      setErrorComment(t("Field is required"));
    }
  }, [form, comment]);

  const onCancel = () => {
    setErrorComment("");
    setComment("");
    onClose();
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={onCancel}>
      <DialogTitle>{t("Reopen Ticket")}</DialogTitle>
      <DialogContent>
        <FormControl
          fullWidth
          sx={(theme) => ({
            marginTop: 2,
            width: "100%",
            "& .MuiFormHelperText-root": {
              color: theme.palette.error.main,
            },
            "& .MuiInputLabel-root::after": {
              content: "'*'",
              color: theme.palette.error.main,
            },
          })}
        >
          <TextField
            id="comments_multiline_textfield"
            label={t("Reason")}
            multiline
            value={comment}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleIssueChange(e.target.value)
            }
          />
          <FormHelperText>{errorComment}</FormHelperText>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="white" onClick={onConfirm}>
          {t("Reopen Ticket")}
        </Button>
        <Button variant="outlined" color="greyed" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReopenCaseDialog;
